import React from 'react';
import 'twin.macro';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import request from '@teetsh/app/src/utils/request';
import { PROGRAMMATIONS_TRASH_KEY } from '@teetsh/app/src/containers/UserSettings/Trash/Programmations/useProgrammationsTrash';
import { progKeys } from 'containers/Programmations/atoms/queryKeys';
import { useTranslation } from 'react-i18next';
import ButtonLink from '@teetsh/app/src/components/Buttons/Link';
import { useBillingInfos } from 'queries/useBillingInfos';
import { LimitReachedErrorResponse } from '@teetsh/app/src/types/errors';

export function useRestoreProgrammation() {
  const { notifySuccess, notifyError } = useNotification();
  const queryClient = useQueryClient();
  const { data: billingInfos } = useBillingInfos();
  const { t } = useTranslation(['common', 'userSettings']);
  const { mutateAsync } = useMutation({
    mutationFn: ({
      id,
      schoolyearId,
      schoolId,
    }: {
      id: string;
      schoolyearId: string;
      schoolId: string;
    }) =>
      request(
        `/schoolyears/${schoolyearId}/schools/${schoolId}/programmations/${id}/restore`,
        { method: 'PATCH' },
      ),
    onSuccess: (_, { id, schoolyearId, schoolId }) => {
      queryClient.invalidateQueries({
        queryKey: [PROGRAMMATIONS_TRASH_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: progKeys.list(schoolyearId, schoolId),
      });
      notifySuccess({
        title: t('userSettings:restore.messages.success'),
        actions: (
          <ButtonLink
            outlined
            intent="success"
            to={`/annee-scolaire/${schoolyearId}/ecole/${schoolId}/programmations/${id}`}
          >
            {t('userSettings:restore.messages.see')}
          </ButtonLink>
        ),
      });
    },
    onError: ({ response }) => {
      if (response?.status === 403 && (response?.data as any)?.requiredPlan) {
        // TODO improve that part (handle !billingInfos)
        const { requiredPlan, limit } =
          response?.data as LimitReachedErrorResponse;
        notifyError({
          title: `${t(
            `common:components.limitReached.description${
              billingInfos?.isOnOldPricing ? 'OnOldPlan' : ''
            }`,
            {
              limit,
              itemLabel: t(
                'common:components.limitReached.labels.programmation',
                {
                  count: limit,
                },
              ),
              currentPlanLabel: billingInfos?.currentPlanLabel,
            },
          )}.`,
          message: (
            <p>
              {t(
                `common:components.limitReached.upgradeHint${
                  billingInfos?.isOnOldPricing ? 'OnOldPlan' : ''
                }`,
                {
                  requiredPlan,
                },
              )}
              .
            </p>
          ),
        });
        return;
      }

      notifyError({ title: t('userSettings:restore.messages.error') });
    },
  });
  return mutateAsync;
}
